import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  inject
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslocoCoreModule } from '@core/transloco/transloco.module';
import { environment } from '@env/environment';
import { AuthService } from '@shared/service/auth.service';
import { CurrentUserQuery } from '@state/auth-state/current-user.query';
import { CurrentUserStore } from '@state/auth-state/current-user.store';
import { CommonUnitTypeStore } from '@state/factory-common-unit-type/common-unit-type.store';
import { filter } from 'rxjs/operators';
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'user',
  templateUrl: './user.component.html',
  imports: [MatButtonModule, MatDividerModule, MatIconModule, MatMenuModule, TranslocoCoreModule]
})
export class UserComponent implements OnInit {
  private cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private route = inject(ActivatedRoute);
  private _router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly currentUserQuery = inject(CurrentUserQuery);
  private readonly currentUserStore = inject(CurrentUserStore);
  private readonly commonUnitTypeStore = inject(CommonUnitTypeStore);
  userId: string;
  currentUserData: any = {};
  currentUserFirstLetter: any;
  accessRole: any;
  currentUrlLastPart: string;
  organizationType: any;
  isGroupFound: boolean = false;
  isAdminUi = false;
  baseUrl = environment.BASE_URL;
  viewAs: any;
  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------
  get isConsultant(): boolean {
    return this.currentUserData?.userType?.toLowerCase() === 'consultant';
  }
  get isSettingOrProfile(): boolean {
    if (this.currentUrlLastPart === 'profile' || this.currentUrlLastPart === 'setting') {
      return true;
    } else {
      return false;
    }
  }
  // @HostListener('window:popstate', ['$event'])
  ngOnInit(): void {
    this.getCurrentUserDataFromState();
    this.getCurrentRouteFromState();
  }
  signOut(): void {
    this.authService.logout();
  }
  goToSetting(): void {
    if (this.isConsultant || this.organizationType === 'brand') {
      this._router.navigate(['settings'], {
        relativeTo: this.route
      });
    } else if (
      this.organizationType === 'factory' &&
      this.accessRole !== 'user' &&
      this.currentUrlLastPart !== 'view-as'
    ) {
      this._router.navigate(['configuration'], {
        relativeTo: this.route
      });
    }
  }
  goToProfile(): void {
    if (this.currentUrlLastPart === 'view-as') {
      this._router.navigate(['../profile'], { relativeTo: this.route });
    } else {
      this._router.navigate(['profile'], { relativeTo: this.route });
    }
  }
  goToViewAs(): void {
    this._router.navigate(['/group/view-as']);
  }
  goBackToAdmin(): void {
    this.currentUserStore.updateOne({
      viewAs: null,
      group: null,
      organization: null
    });
    this.commonUnitTypeStore.resetStoreData();
    localStorage.removeItem('unitType');
    localStorage.removeItem('filterData');
    localStorage.removeItem('selectedFactoriesToCompare');
    window.location.href = `${environment.SUPPLIER_WEB_ENDPOINT}`;
  }
  private getCurrentUserDataFromState(): void {
    this.currentUserQuery
      .select(state => state.data)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((currentUserData: any) => {
        if (currentUserData?.userType) {
          this.viewAs = currentUserData.viewAs;
          this.currentUserData = currentUserData;
          this.accessRole = currentUserData.userRole?.toLowerCase();
          this.organizationType = currentUserData.userType?.toLowerCase();
          if (currentUserData.group) {
            this.isGroupFound = true;
          }
          this.isAdminUi = this.organizationType === 'admin';
          this.cdr.markForCheck();
        }
      });
  }
  private getCurrentRouteFromState(): void {
    const updateUrl = (url: string) => {
      const urlParts = url.split('/');
      this.currentUrlLastPart = urlParts.at(-1);
      this.cdr.markForCheck();
    };

    updateUrl(this._router.url);

    this._router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(e => {
        updateUrl(e.urlAfterRedirects || e.url);
      });
  }
  goToActivityLog() {
    this._router.navigate(['user-activity-log'], {
      relativeTo: this.route
    });
  }
}
